import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";

/**
 * Rich-tag variant of {@link TagsWithPopover} for the post-KD-563 picker
 * world. Renders {@link ResourceTagSummary} chips (with `color`,
 * `category`) instead of plain strings, but keeps the same visible-cap
 * + portaled hover popover mechanic so a service detail card never
 * grows vertically when a user attaches many tags.
 *
 * Used by `EditableTagList`'s `PickerChips` mode. The legacy free-text
 * chips path stays on `TagsWithPopover` directly because there's no
 * color or category to preserve.
 */

interface RichTagsWithPopoverProps {
  tags: ResourceTagSummary[];
  /** Maximum chips rendered inline before truncating. Default 3. */
  maxVisible?: number;
  /** Force the trigger row to stay on a single line. */
  triggerSingleRow?: boolean;
  /**
   * When true, inline chips size to their label (`w-fit`) and only cap width
   * when the name is long enough to hit `max-w`. Used by detail-card strips.
   */
  constrainToContainer?: boolean;
}

const FALLBACK_DOT = "var(--color-primitive-gray-300)";

function chipTitle(tag: ResourceTagSummary): string {
  if (tag.category?.name) {
    return `${tag.name} (${tag.category.name})`;
  }
  return tag.name;
}

/**
 * Single rich tag chip — kept identical to the legacy `PickerChips`
 * markup so the visual style is consistent inline and inside the
 * popover.
 */
const RichTagChip: React.FC<{
  tag: ResourceTagSummary;
  variant?: "inline" | "popover";
  constrain?: boolean;
}> = ({ tag, variant = "inline", constrain = false }) => {
  const isPopover = variant === "popover";

  return (
    <span
      className={`relative inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-semantic-text-primary border border-semantic-border-secondary overflow-hidden ${
        isPopover
          ? "flex-shrink-0 max-w-[280px] h-auto min-h-6 py-1 whitespace-normal break-words"
          : constrain
            ? "w-fit max-w-[200px] min-w-0 h-6 flex-shrink"
            : "flex-shrink-0 max-w-[200px] h-6"
      }`}
      style={tag.color ? { borderColor: tag.color } : undefined}
      title={chipTitle(tag)}
    >
      <span
        aria-hidden
        className="absolute inset-0 opacity-40"
        style={{ background: tag.color ?? FALLBACK_DOT }}
      />
      <span
        className={`relative ${
          isPopover
            ? "whitespace-normal break-words"
            : constrain
              ? "truncate"
              : "flex-1 min-w-0 truncate"
        }`}
      >
        {tag.name}
      </span>
    </span>
  );
};

export const RichTagsWithPopover: React.FC<RichTagsWithPopoverProps> = ({
  tags,
  maxVisible = 3,
  triggerSingleRow = false,
  constrainToContainer = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [rect, setRect] = useState<DOMRect>();
  const triggerRef = useRef<HTMLDivElement>(null);
  // Small delay before hide so the cursor can travel from the trigger
  // into the popover without the popover collapsing. Mirrors
  // TagsWithPopover so the two components feel identical.
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    if (isHovered && triggerRef.current) {
      setRect(triggerRef.current.getBoundingClientRect());
    }
  }, [isHovered]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const handleEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsHovered(true);
  };

  const handleLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
      hideTimeoutRef.current = null;
    }, 150);
  };

  if (tags.length === 0) return null;

  const visible = tags.slice(0, maxVisible);
  const remaining = tags.slice(maxVisible);
  const showMore = remaining.length > 0;
  const shouldShowPopover = isHovered && showMore;

  const popoverStyle: React.CSSProperties = {
    position: "fixed",
    zIndex: 9999,
    maxWidth: "300px",
  };
  if (rect && shouldShowPopover) {
    const gap = 8;
    popoverStyle.top = rect.bottom + gap;
    popoverStyle.left = rect.left;
  }

  const triggerOverflowClass = constrainToContainer
    ? "overflow-hidden"
    : "overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]";

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        className={`flex items-center gap-2 justify-end min-w-0 ${
          triggerSingleRow ? "flex-nowrap" : "flex-wrap"
        } ${triggerOverflowClass}`}
      >
        {visible.map((tag) => (
          <RichTagChip
            key={tag.id}
            tag={tag}
            variant="inline"
            constrain={constrainToContainer}
          />
        ))}
        {showMore && (
          <span
            className="inline-flex items-center justify-center rounded-md px-2 py-1 h-6 text-xs flex-shrink-0 text-semantic-text-secondary bg-semantic-bg-secondary border border-semantic-border-secondary"
            aria-label={`${remaining.length} more tags`}
          >
            +{remaining.length}
          </span>
        )}
      </div>

      {shouldShowPopover &&
        createPortal(
          <div
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            style={popoverStyle}
            className="
              bg-semantic-bg-primary
              rounded-md
              p-3
              shadow-lg
              border border-semantic-border-primary
              min-w-[200px]
            "
          >
            <div className="flex flex-wrap gap-1.5">
              {remaining.map((tag) => (
                <RichTagChip key={tag.id} tag={tag} variant="popover" />
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
